// src/data/toolkitData.js — the content the toolkit tools run on.
//
// Lifted verbatim from the Dive In modules so a tool never teaches something
// its module does not: the family story styles and the Real-Talk formula from
// Module 1; the GENTLE interview method, conversation starters and navigation
// tips from Module 2; the five ingredients from Module 3; and the question
// types from Module 4. Change a module, change the matching block here.

// Module 1 — Spot Your Family's Story Style
export const FAMILY_STORY_STYLES = [
  {
    'name': 'The Glow-Up Squad',
    'motto': "Everything's about overcoming and winning",
    'strengths': [
      'Great at finding growth and learning in challenges',
      'Builds confidence and resilience mindset',
      'Celebrates family achievements and progress',
    ],
    'blindSpots': [
      'Sometimes skips acknowledging real pain or difficulty',
      "Can make family members feel pressure to 'get over' things quickly",
      'May miss the value of processing emotions fully',
    ],
    'soundsLike': "When Mum lost her job, she used it as motivation to start her own business and now she's more successful than ever!",
  },
  {
    'name': 'The Keep-It-Real Crew',
    'motto': 'Focus on struggles and injustices',
    'strengths': [
      'Excellent at validating difficult emotions and experiences',
      'Builds empathy and emotional intelligence',
      "Doesn't sugarcoat or minimise real problems",
    ],
    'blindSpots': [
      'Sometimes gets stuck in problem-focus without finding growth',
      'Can accidentally reinforce victim mindset',
      'May miss opportunities to build hope and agency',
    ],
    'soundsLike': "The system is rigged against families like ours. Mum's boss treated her terribly and now we're all paying the price.",
  },
  {
    'name': 'The Lesson Learned League',
    'motto': 'Every experience has a moral',
    'strengths': [
      'Great at extracting wisdom and teaching moments',
      'Builds family identity around shared values',
      'Helps family members learn from experiences',
    ],
    'blindSpots': [
      'Can turn every story into a lecture or teaching moment',
      'Sometimes misses the emotional processing part',
      'May make family members feel judged for their choices',
    ],
    'soundsLike': 'When Mum lost her job, it taught us that we need to always save money and never depend on one income source.',
  },
  {
    'name': 'The Just-The-Facts Family',
    'motto': 'Stick to who, what, when, where',
    'strengths': [
      'Clear, accurate information sharing',
      'Avoids drama and emotional overwhelm',
      'Focuses on practical problem-solving',
    ],
    'blindSpots': [
      'Misses the emotional bonding power of storytelling',
      'Can feel distant or disconnected',
      "Doesn't build meaning or family identity through narratives",
    ],
    'soundsLike': 'Mum lost her job in March. She applied to new places. She got hired at the new company in May.',
  },
  {
    'name': 'The Drama Dynasty',
    'motto': "Everything's intense and high-stakes",
    'strengths': [
      'Great emotional engagement and family bonding',
      'Makes every experience feel important and meaningful',
      'Builds family identity around shared intensity',
    ],
    'blindSpots': [
      'Can exhaust family members with constant high emotion',
      "Sometimes loses perspective on what's actually serious",
      'May create anxiety or stress around normal life events',
    ],
    'soundsLike': "When Mum lost her job, it was absolutely devastating! Our whole world was turned upside down! We didn't know if we'd survive!",
  },
  {
    'name': 'The Smooth-Over Squad',
    'motto': "Avoid negativity, we don't dwell",
    'strengths': [
      'Maintains family harmony and positive atmosphere',
      'Focuses on moving forward rather than staying stuck',
      'Protects family members from overwhelming emotions',
    ],
    'blindSpots': [
      'Misses opportunities for emotional processing and bonding',
      'Can make family members feel unheard or invalidated',
      "Doesn't build resilience through facing challenges together",
    ],
    'soundsLike': "Mum's job thing? Oh, we don't really talk about that. Everything worked out fine. Let's focus on positive things!",
  },
];

// Module 1 — the Real-Talk Story Formula, the balance the module aims at
export const REAL_TALK_FORMULA = [
  { name: 'Truth-Telling', description: 'Acknowledge what actually happened - the real emotions, real challenges, real impact' },
  { name: 'Growth-Spotting', description: 'Find what your family learned, gained, or discovered through the experience' },
  { name: 'Future-Building', description: 'Connect past struggles to current family strength and wisdom' },
];

// Module 2 — the GENTLE method for interviewing an older relative
export const GENTLE_METHOD = [
  {
    'key': 'g_getPermission',
    'step': 'Get Permission',
    'description': "Approach respectfully and ask if they're interested in sharing",
    'examples': [
      "I've been thinking about family history lately - would you be interested in telling me some stories?",
      "I realise I don't know much about what life was like when you were young. Could you teach me?",
      "I'm trying to understand our family better - would you help me learn about our story?",
      'Would it be OK if I recorded this, just for the family? You can say no, skip anything, or stop any time.',
    ],
  },
  {
    'key': 'e_expressCuriosity',
    'step': 'Express Genuine Curiosity',
    'description': 'Show real interest, not just polite attention',
    'examples': [
      "I can't imagine what it must have been like to [specific experience]",
      'That sounds like such a different world - tell me more about...',
      "I've never heard about this part of your life - what was that like?",
    ],
  },
  {
    'key': 'n_noticeEnergy',
    'step': 'Notice Their Energy',
    'description': "Pay attention to what they're excited or emotional about",
    'tips': [
      'Follow topics that make their eyes light up',
      'Give extra time to stories that seem important to them',
      'Notice if they seem tired or overwhelmed and take breaks',
      'Respect topics they seem reluctant to discuss',
    ],
  },
  {
    'key': 't_thankThem',
    'step': 'Thank Them Genuinely',
    'description': "Acknowledge the gift they're giving you",
    'examples': [
      'Thank you for sharing that with me - I had no idea about...',
      'I feel like I understand you/our family so much better now',
      "These stories are helping me see our family's strength",
      "I'm so grateful you're willing to teach me about this",
    ],
  },
  {
    'key': 'l_listenActively',
    'step': 'Listen Actively',
    'description': "Be fully present. If you're recording, prop the phone up, start it and leave it alone - no checking, no scrolling",
    'techniques': [
      "Make eye contact and use body language that shows you're engaged",
      "Ask follow-up questions that show you're really listening",
      "Reflect back what you heard: 'So it sounds like...'",
      "Share your reactions: 'That must have been scary/exciting/difficult'",
      'Wait. Leave a pause after they finish - the best part often comes next',
      "When something sparks, say 'Tell me more about that'",
    ],
  },
  {
    'key': 'e_extractWisdom',
    'step': 'Extract the Wisdom',
    'description': 'Help them recognise the insights in their experiences',
    'questions': [
      'What did that experience teach you?',
      'How did going through that change you?',
      'What would you want someone my age to know about [topic]?',
      'What are you most proud of from that time in your life?',
      "What should I have asked you that I didn't?",
    ],
  },
];

// Module 2 — conversation starters, by theme
export const CONVERSATION_STARTERS = [
  {
    'key': 'timeAndPlace',
    'questions': [
      'What was [your town/neighbourhood] like when you were my age?',
      'What did a typical day look like for you when you were in your twenties?',
      "What's something about life back then that would surprise me?",
      'How did people meet and date when you were young?',
    ],
  },
  {
    'key': 'changeAndAdaptation',
    'questions': [
      "What's the biggest change you've seen in your lifetime?",
      'What was it like when [major technology/social change] happened?',
      "How did your parents' generation differ from yours?",
      "What advice did your parents give you that you didn't follow?",
    ],
  },
  {
    'key': 'personalJourney',
    'questions': [
      "What's something you did that took courage?",
      'Tell me about a time you surprised yourself',
      "What's a decision you made that changed everything?",
      "What's something you're proud of that people might not know about?",
    ],
  },
  {
    'key': 'familyConnections',
    'questions': [
      'What was my parent like as a child/teenager?',
      "What are some family traits or characteristics you've noticed across generations?",
      "What's a family story you think I should know?",
      'What do you hope future generations of our family remember about this time?',
    ],
  },
];

// Module 2 — what to do when an interview wanders
export const NAVIGATION_TIPS = [
  {
    'key': 'ifTheyResist',
    'tips': [
      'Start with lighter topics and build trust gradually',
      'Share something about your own life first',
      'Ask for their advice rather than their stories initially',
      'Respect their boundaries and try again later',
    ],
  },
  {
    'key': 'ifTheyRamble',
    'tips': [
      'Gently guide them back with specific questions',
      "Show appreciation for all parts of what they're sharing",
      'Ask clarifying questions about the parts you want to understand better',
      "Remember that sometimes the 'rambling' contains important context",
    ],
  },
  {
    'key': 'ifTheyGetEmotional',
    'tips': [
      'Acknowledge their emotions with empathy',
      "Ask if they'd like to take a break or continue",
      'Thank them for trusting you with difficult memories',
      "Don't rush to 'fix' their emotions - just witness them",
    ],
  },
  {
    'key': 'ifTheyForgetDetails',
    'tips': [
      'Focus on how things felt rather than exact facts',
      'Ask about the parts they do remember clearly',
      'Use photos or objects to help trigger memories',
      'Validate that the emotions and impressions matter more than perfect accuracy',
    ],
  },
];

// Module 3 — the five story ingredients
export const STORY_INGREDIENTS = [
  {
    'name': 'Real Emotions',
    'symbol': '❤️',
    'description': 'Honest feelings without judgement or minimisation',
    'whatItIncludes': [
      "Naming specific emotions rather than using vague words like 'fine' or 'bad'",
      'Acknowledging that multiple emotions can exist at the same time',
      'Including both negative and positive emotions without judgement',
      'Recognising that emotions change over time as people process experiences',
    ],
    'whyItMatters': [
      'Emotions are what make stories relatable and memorable',
      'Sharing emotions honestly can build empathy and connection',
      'Feelings that never get talked about can leave people feeling alone, even in a close family',
      'Hearing emotions named in stories gives kids words for their own feelings',
    ],
    'commonlyMissing': [
      'Stories that focus only on facts without emotional context',
      "Stories that judge emotions as 'good' or 'bad'",
      'Stories that skip the emotional processing journey',
      'Stories that present only one emotion when the experience was complex',
    ],
  },
  {
    'name': 'Agency & Choice',
    'symbol': '⚡',
    'description': 'Recognition of what choices were available and what was chosen',
    'whatItIncludes': [
      'Acknowledging the constraints and limitations that existed',
      'Highlighting the choices that were available within those constraints',
      'Recognising creative problem-solving and resourcefulness',
      'Understanding that agency can be small actions within big circumstances',
    ],
    'whyItMatters': [
      'Noticing the choices people made can ease feelings of helplessness',
      'Understanding choice helps people learn from experiences',
      "Spotting small choices in hard situations can remind people they weren't completely powerless",
      'Kids learn that they often have some choices, even in challenging circumstances',
    ],
    'commonlyMissing': [
      'Stories that focus only on what happened TO the family',
      'Stories that miss the creative solutions family members found',
      'Stories that present family members as purely victims or heroes',
      "Stories that don't acknowledge the constraints people were working within",
    ],
  },
  {
    'name': 'Growth & Learning',
    'symbol': '🌱',
    'description': 'Genuine development without minimising the difficulty',
    'whatItIncludes': [
      'Skills, insights, or strengths that developed through the experience',
      'Changes in perspective or understanding',
      'Increased capacity to handle similar situations',
      'Deeper relationships or self-knowledge that emerged',
    ],
    'whyItMatters': [
      'Stories about growth can offer hope',
      'Learning from experiences helps families face future challenges',
      'Growth stories show that difficulty can sometimes lead to positive change',
      'Kids learn that challenges can be chances to learn - without being told they should be grateful for hard things',
    ],
    'commonlyMissing': [
      'Stories that focus only on surviving without acknowledging growth',
      "Stories that fake growth or learning that didn't actually happen",
      'Stories that present growth as immediate rather than gradual',
      'Stories that make growth seem easy or inevitable',
    ],
  },
  {
    'name': 'Family Values Connection',
    'symbol': '🏠',
    'description': 'How the experience reflects or reveals what matters to your family',
    'whatItIncludes': [
      'Connections to family identity and what you stand for',
      'How the experience reinforced or challenged family values',
      'Ways the situation revealed family strengths and priorities',
      'Links between individual experiences and family character',
    ],
    'whyItMatters': [
      'Values connections can strengthen family identity and belonging',
      'Understanding family values helps guide future decisions',
      'Values-based stories can help people find meaning in difficult experiences',
      'Kids learn what their family stands for through values-connected stories',
    ],
    'commonlyMissing': [
      'Stories told as isolated events without family context',
      "Stories that don't connect to broader family identity",
      'Stories that miss opportunities to reinforce positive values',
      "Stories that don't help family members understand who they are together",
    ],
  },
  {
    'name': 'Time & Healing Perspective',
    'symbol': '⏰',
    'description': 'Recognition that healing and understanding unfold over time',
    'whatItIncludes': [
      'Acknowledgement that initial reactions might have been different from current understanding',
      'Recognition that healing is a process, not an event',
      'Understanding that perspectives can change as people grow',
      'Appreciation for the journey of recovery, adaptation, or growth',
    ],
    'whyItMatters': [
      'Time perspective prevents rushing healing or growth processes',
      'Understanding change over time builds patience with difficult experiences',
      'Temporal awareness helps people not get stuck in initial reactions',
      "Kids learn that healing and growth take time and that's normal",
    ],
    'commonlyMissing': [
      'Stories that present only the immediate aftermath of events',
      'Stories that suggest healing should be quick or linear',
      "Stories that don't acknowledge how understanding has evolved",
      'Stories that miss the ongoing impact and continued growth',
    ],
  },
];

// Module 4 — question types that invite a story instead of an answer
export const QUESTION_TYPES = [
  {
    'name': 'Story Invitation Questions',
    'purpose': 'Invite narrative sharing instead of simple answers',
    'examples': [
      "What's the story behind that?",
      'Tell me about a time when...',
      'What was it like when you...',
      'Walk me through how that happened',
    ],
    'whyTheyWork': 'They naturally invite the five story ingredients and create space for emotional sharing',
    'bestFor': 'Family members who enjoy talking but need prompting to go deeper',
  },
  {
    'name': 'Emotion Exploration Questions',
    'purpose': 'Invite feelings and personal reactions',
    'examples': [
      'How did that feel for you?',
      'What was going through your mind when...',
      'What surprised you most about...',
      'What was the best/hardest part of...',
    ],
    'whyTheyWork': 'They validate emotional experiences and build empathy between family members',
    'bestFor': 'Building emotional intelligence and connection in the family',
  },
  {
    'name': 'Growth and Learning Questions',
    'purpose': 'Explore development, insights, and wisdom',
    'examples': [
      'What did you learn from that experience?',
      'How has that changed you?',
      'What would you do differently now?',
      'What advice would you give someone facing something similar?',
    ],
    'whyTheyWork': 'They help family members recognise their own growth and share wisdom with each other',
    'bestFor': 'Processing challenges and building family resilience narratives',
  },
  {
    'name': 'Values and Identity Questions',
    'purpose': 'Explore what matters most and family character',
    'examples': [
      "What does that say about what's important to you?",
      'What are you most proud of about how you handled that?',
      'What family trait do you see in that story?',
      'How does that reflect what our family stands for?',
    ],
    'whyTheyWork': 'They build family identity and help everyone understand shared values',
    'bestFor': 'Strengthening family cohesion and identity during transitions or conflicts',
  },
  {
    'name': 'Future and Dream Questions',
    'purpose': 'Explore hopes, goals, and possibilities',
    'examples': [
      'What are you excited about coming up?',
      'What would you love to try or learn?',
      "What's a dream you have that maybe we don't know about?",
      'What do you hope for our family in the future?',
    ],
    'whyTheyWork': "They create positive energy and help family members support each other's aspirations",
    'bestFor': 'Building excitement and mutual support around individual and family goals',
  },
];
